import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "react-oauth2-code-pkce";

import { analytics } from "~/App";

const FirebaseAuthContext = createContext(null);

export const FirebaseAuthProvider = ({ children }) => {
  const { token, logIn, logOut } = useContext(AuthContext);
  const [checkedAuth, setCheckedAuth] = useState(false);
  const [user, setUser] = useState();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
      } else {
        setUser(null);
      }
      setCheckedAuth(true);
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackSignIn = (user) => {
    const traits = {
      first_name: user.given_name,
      last_name: user.family_name,
      email: user.email,
    };

    analytics.identify({ traits }, { userId: user.sub });
    analytics.track("User Signed In", { traits }, { userId: user.sub });
  };

  useEffect(() => {
    const controller = new AbortController();
    if (!checkedAuth) {
      return;
    }

    if (token && checkedAuth && !user) {
      const signInFirebase = async () => {
        const url = "/.netlify/functions/createCustomToken";
        try {
          const response = await fetch(url, {
            body: JSON.stringify({ token }),
            headers: { "Content-Type": "application/json" },
            method: "POST",
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error(`${response.status}: ${response.statusText}`);
          }

          const { firebaseToken, user } = await response.json();

          if (controller.signal.aborted) return;
          await signInWithCustomToken(auth, firebaseToken);

          if (controller.signal.aborted) return;
          trackSignIn(user);
        } catch (error) {
          if (!controller.signal.aborted) {
            alert("Failed to log in.");
            logOut();
          }
        }
      };
      signInFirebase();
    }
    return () => {
      controller.abort();
    };
  }, [auth, checkedAuth, logOut, user, token]);

  return (
    <FirebaseAuthContext.Provider
      value={{
        checkedAuth,
        logIn,
        logOut,
        user,
      }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  );
};

export const useAuth = () => useContext(FirebaseAuthContext);
